import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  chatReducer,
  initialState as chatInitialState,
  createSession,
  setCurrentSession,
  setSessions,
  resetSession,
  setLoadingState,
} from "./store/chatSlice";
import {
  ChatbotProviderProps,
  ChatEventStatus,
  ChatSession,
  ChatState,
  LoadingState,
} from "./types";
import { UnknownAction } from "@reduxjs/toolkit";
import { ChatbotV2 } from "./components";
import { PromptInputProvider } from "@altimateai/lego";

export interface ChatContextType extends ChatState {
  dispatch: React.Dispatch<UnknownAction>;
}

export interface ChatProviderRef {
  resetSession: () => void;
  setUserInput: (text: string) => void;
  resetStatus: () => void;
  getSessionById: (_sessionId: string) => ChatSession | null;
  getSessionId: () => string | null;
}

export const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider = forwardRef<ChatProviderRef, ChatbotProviderProps>(
  (
    {
      enableStudioWorkspaces,
      taskLabel,
      initialValidation,
      urls,
      provider,
      disableContext,
      requestParams,
      components,
      helloMessage,
      assistantMeta,
      context,
      contextOptions,
      commandHints,
      fileUploadProps,
      isMarkdownResponse,
      models,
      placeholder,
      autoFocus,
      classNames,
      sessions,
      currentSessionId,
      initialMessage,
      submitInitialMessageOnMount,
      questionFormLeftActions,
      refetchSession,
      chatboxStartPosition,
      helloMessageFooter,
      handleSavePrompt,
      showSavePromptButton,
      onEvent,
    },
    ref
  ) => {
    const [state, dispatch] = useReducer(chatReducer, {
      ...chatInitialState,
      sessions: sessions || {},
      currentSessionId: currentSessionId || null,
    });

    // Ref to access PromptInputProvider's setInput function
    const setInputRef = useRef<((_text: string) => void) | undefined>();

    useImperativeHandle(ref, () => ({
      getSessionById: (sessionId: string) => {
        return state.sessions[sessionId];
      },
      getSessionId: () => {
        return state.currentSessionId;
      },
      // delete current session id and create a new session
      resetSession: () => {
        dispatch(resetSession());
        setTimeout(() => {
          dispatch(createSession({ context, models }));
        }, 0);
      },
      setUserInput: (text: string) => {
        if (state.currentSessionId && setInputRef.current) {
          setInputRef.current(text);
        }
      },
      resetStatus: () => {
        dispatch(
          setLoadingState({
            state: LoadingState.UNINITIALIZED,
            sessionId: state.currentSessionId,
          })
        );
      },
    }));

    useEffect(() => {
      if (sessions && Object.keys(sessions || {}).length > 0) {
        dispatch(setSessions(sessions));
      }
    }, [sessions]);

    useEffect(() => {
      if (!currentSessionId) {
        dispatch(createSession({ context, models }));
      } else {
        dispatch(setCurrentSession(currentSessionId));
      }
    }, [currentSessionId, dispatch, context, models]);

    // Stash onEvent in a ref so changing its identity does not re-fire the
    // notification effects below. Re-firing on identity change is what caused
    // session_created to be echoed back to the parent and produce a feedback loop.
    const onEventRef = useRef(onEvent);
    useEffect(() => {
      onEventRef.current = onEvent;
    });

    // Notify parent when WE create a session internally. Skip echoing back the
    // value the parent just gave us via currentSessionId — that would tell the
    // parent to set the session it already has.
    useEffect(() => {
      if (state.currentSessionId && state.currentSessionId !== currentSessionId) {
        onEventRef.current?.({
          type: "session_created",
          sessionId: state.currentSessionId,
        });
      }
      // currentSessionId is read intentionally without subscribing — we only
      // want to fire when our internal state changes, not on every prop change.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.currentSessionId]);

    // Notify parent when loading state changes
    useEffect(() => {
      if (state.currentSessionId) {
        const statusMap: Record<LoadingState, ChatEventStatus> = {
          [LoadingState.UNINITIALIZED]: "idle",
          [LoadingState.LOADING]: "pending",
          [LoadingState.LOADED]: "complete",
          [LoadingState.ERROR]: "error",
          [LoadingState.CANCELLED]: "cancelled",
        };
        onEventRef.current?.({
          type: "status_change",
          status: statusMap[state.loadingState],
          sessionId: state.currentSessionId,
        });
      }
    }, [state.loadingState, state.currentSessionId]);

    return (
      <ChatContext.Provider
        value={{
          ...state,
          enableStudioWorkspaces,
          taskLabel,
          initialValidation,
          urls,
          provider,
          dispatch,
          disableContext,
          requestParams,
          components,
          isMarkdownResponse,
          models,
          classNames,
        }}
      >
        <PromptInputProvider initialInput={initialMessage} setInputRef={setInputRef}>
          <ChatbotV2
            assistantMeta={assistantMeta}
            helloMessage={helloMessage}
            fileUploadProps={fileUploadProps}
            context={context}
            contextOptions={contextOptions}
            commandHints={commandHints}
            placeholder={placeholder}
            autoFocus={autoFocus}
            initialMessage={initialMessage}
            submitInitialMessageOnMount={submitInitialMessageOnMount}
            questionFormLeftActions={questionFormLeftActions}
            refetchSession={refetchSession}
            chatboxStartPosition={chatboxStartPosition}
            helloMessageFooter={helloMessageFooter}
            handleSavePrompt={handleSavePrompt}
            showSavePromptButton={showSavePromptButton}
          />
        </PromptInputProvider>
      </ChatContext.Provider>
    );
  }
);

ChatProvider.displayName = "ChatProvider";
